import { useCallback, useEffect, useState } from 'react';
import { stateSetter } from '@/utils';
import { IServerColumnConfig } from '@/components/table/column-config';
import {
	deleteStatus,
	fetchStatuses,
	fetchStatusesTableConfig,
	patchStatus,
	postStatus
} from './actions';
import { IProjectStatus, IProjectStatusPayload, ProjectStatusesReturnType } from './types';

export function useProjectStatuses(
	getOnRender: boolean | undefined = undefined,
	queryParam: string | undefined = undefined,
	itemsSet = 10,
	/**
	 * Paginarea INIȚIALĂ (de regulă cea din URL, via useListQueryState). Fără ea,
	 * starea internă pornește pe 0/10 și primul fetch pleacă cu valori greșite, urmat
	 * de un al doilea după sincronizarea din efectele-punte — două call-uri și un race
	 * pe răspunsuri. Doar valoarea de la primul render contează.
	 */
	initialPagination: { page: number; items: number } | undefined = undefined,
	withTableConfig = false
): ProjectStatusesReturnType {
	const [data, setData] = useState<IProjectStatus[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	// Configurația de coloane (api/project-stages/table-config) — cerută doar de pagina
	// de listă (withTableConfig); la eroare rămâne null și tabelul cade silențios pe
	// coloanele definite local.
	const [tableConfig, setTableConfig] = useState<IServerColumnConfig[] | null>(null);
	const [loadingTableConfig, setLoadingTableConfig] = useState<boolean>(
		Boolean(withTableConfig)
	);
	const [loadingCreate, setLoadingCreate] = useState<boolean>(false);
	const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
	const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
	const [enableQuery, setEnableQuery] = useState<boolean>(false);
	const [page, setPage] = useState<number>(initialPagination?.page ?? 0);
	const [items, setItems] = useState<number>(initialPagination?.items ?? itemsSet);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(0);

	const handleResponse = (pageParam: number, itemsParam: number): void => {
		setTotalPages(pageParam);
		setTotalItems(itemsParam);
	};

	useEffect(() => {
		if (!withTableConfig) return undefined;
		let active = true;
		setLoadingTableConfig(true);
		fetchStatusesTableConfig()
			.then((columns) => {
				if (active) setTableConfig(columns.length ? columns : null);
			})
			.catch(() => {
				// Fallback silențios: fără config BE, tabelul folosește coloanele locale.
				if (active) setTableConfig(null);
			})
			.finally(() => {
				if (active) setLoadingTableConfig(false);
			});
		return () => {
			active = false;
		};
	}, [withTableConfig]);

	const getStatuses = useCallback(async (pageParam: number, itemsParam: number) => {
		try {
			setLoading(true);
			const response = await fetchStatuses('', pageParam, itemsParam, handleResponse);
			setData(response);
		} catch {
			// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
		} finally {
			setLoading(false);
		}
	}, []);

	const getStatusesQuery = useCallback(
		async (query: string, pageParam: number, itemsParam: number) => {
			try {
				if (!enableQuery) setEnableQuery(true);
				setLoading(true);
				const response = await fetchStatuses(query, pageParam, itemsParam, handleResponse);
				setData(response);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		[enableQuery]
	);

	const createStatus = useCallback(
		async (payload: IProjectStatusPayload, onSuccess?: () => void) => {
			try {
				setLoadingCreate(true);
				const response = await postStatus(payload);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingCreate(false);
			}
		},
		[]
	);

	const editStatus = useCallback(
		async (statusId: number | string, payload: IProjectStatusPayload, onSuccess?: () => void) => {
			try {
				setLoadingUpdate(true);
				const response = await patchStatus(statusId, payload);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingUpdate(false);
			}
		},
		[]
	);

	const removeStatus = useCallback(
		async (statusId: number | string, onSuccess?: () => void) => {
			try {
				setLoadingDelete(true);
				const response = await deleteStatus(statusId);
				if (typeof response === 'number' && response >= 200 && response < 300) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingDelete(false);
			}
		},
		[]
	);

	useEffect(() => {
		if (getOnRender && queryParam === undefined) {
			getStatuses(page, items);
		}

	}, [getOnRender, page, items]);

	useEffect(() => {
		if (queryParam !== undefined) {
			getStatusesQuery(queryParam, page, items);
		} else if (enableQuery) {
			getStatuses(page, items);
		}

	}, [queryParam, page, items]);

	return {
		data,
		setData: setData as stateSetter<IProjectStatus[]>,
		loading,
		tableConfig,
		loadingTableConfig,
		loadingCreate,
		loadingUpdate,
		loadingDelete,
		getStatuses,
		getStatusesQuery,
		createStatus,
		editStatus,
		removeStatus,
		page,
		items,
		setPage,
		setItems,
		totalPages,
		totalItems
	};
}
